// React
import React, {Component} from 'react';
import PropTypes from 'prop-types';

// Amplify
import {Auth} from 'aws-amplify';

// Routing
import {navigate} from '_routes/RootNavigation';
import {getQueryStringParameters} from '_util';

// Redux
import {connect} from 'react-redux';
import {INVITE_CODE_KEY} from '_redux/user/utils';

// Components
import
  AccessControl,
{ADMIN, MANUFACTURER, CREW_LEADER, CREW_MEMBER, DEALER, OWNER}
  from '_components/auth/AccessControl.component';
import InviteModalContent from '_components/common/InviteModalContent.component';
import Modal from '_components/common/Modal.component';
import Notifications from '_components/Notifications.component';

// UI Framework
import {
  Spinner,
  Text,
  View,
} from 'native-base';
import {
  AsyncStorage,
  Image,
  TouchableOpacity,
} from 'react-native';

// Style
import {styles as global} from '_style/Global.style';
import {styles} from '_pages/Home.style';
import {AsyncStorage as localStorage} from "react-native-web/src";

/**
 * NavItem is the component on the Home Page that links
 * to other screens. These are the clickable links left
 * of the notifications.
 */
class NavItem extends Component {
  /**
   * Constructor
   *
   * @param {*} props
   */
  constructor(props) {
    super(props);
    this._hasRedirected = false;

    this._navigate = this._navigate.bind(this);
  }

  /**
   * Will navigate the user to another screen in the application based
   * on the route that was supplied as a prop to this component.
   */
  _navigate() {
    const {route, onPress} = this.props;

    if (onPress) {
      onPress();
    } else {
      navigate(route);
    }
  }

  /**
   * Component Render
   */
  render() {
    const {route, icon} = this.props;

    return (
      <TouchableOpacity onPress={this._navigate}>
        <View style={[styles.navItem, global.dropShadow]}>
          <View style={[styles.navItemIcon]}>
            {icon}
          </View>
          <Text style={[styles.navItemText]}>{route}</Text>
        </View>
      </TouchableOpacity>
    );
  }
}

NavItem.propTypes = {
  route: PropTypes.string.isRequired,
  icon: PropTypes.any.isRequired,
  onPress: PropTypes.func,
};

/**
 * Home Page.
 */
class Home extends Component {

  /**
   * Constructor.
   *
   * @param {*} props
   */
  constructor(props) {
    super(props);

    this.state = {
      showInviteModal: false, // true to show the invite modal (found an invite code).
    };

    this._signOut = this._signOut.bind(this);
    this._openDrawer = this._openDrawer.bind(this);

    this._showInviteModal = this._showInviteModal.bind(this);
    this._hideInviteModal = this._hideInviteModal.bind(this);
  }

  /**
   * Check if there is an invite code, if so, we need to show the join modal
   * and query the backend to check for possible error state (e.g. user is
   * already a part of a crew or they are an owner with devices.)
   *
   * @param {*} prevProps
   */
  async componentDidMount() {
    const queryStringParameters = getQueryStringParameters(window.location.href);

    if (queryStringParameters.inviteCode) {
      await this._logout(queryStringParameters.inviteCode);
      window.location.reload();
    }
  }

  async _logout(inviteCode) {
    try {
      const isOldDevice = await localStorage.getItem("updateRequired") === "true";

      await localStorage.clear();
      if (isOldDevice) {
        await localStorage.setItem("updateRequired", "true");
      }
      if (inviteCode){
        await localStorage.setItem("inviteCode", inviteCode);
      }
      await Auth.signOut();
      console.log("success signing out");
    } catch (error) {
      console.log('error signing out: ', error);
    }
  }

  /**
   * Shows the invite modal.
   */
  _showInviteModal() {
    this.setState({
      showInviteModal: true,
    });
  }

  /**
   * Hides the invite modal.
   */
  _hideInviteModal() {
    this.setState({
      showInviteModal: false,
    });
  }

  /**
   * Sign the user out of the app. Should route them to the
   * sign in / sign up screen.
   */
  _signOut() {
    Auth.signOut();
  }

  /**
   * Open up the side drawer.
   */
  _openDrawer() {
    this.props.navigation.openDrawer();
  }

  /**
   +   * As soon as we finish fetching user info, auto-redirect if their group
   +   * is OWNER, CREW_LEADER or CREW_MEMBER.
   +   */
  async componentDidUpdate(prevProps) {
    const already = await AsyncStorage.getItem('hasAutoRedirected');
    if (already) return;

    // If there is a pending invite code, the InviteModal needs to finish its
    // checkEligibility -> addCrewMember flow before we navigate away. Skip
    // the auto-redirect until the invite code has been cleared.
    const pendingInviteCode = await AsyncStorage.getItem(INVITE_CODE_KEY);
    if (pendingInviteCode || this.state.showInviteModal) return;

    if (prevProps.isFetchingUserInfo && !this.props.isFetchingUserInfo) {
      const {userInformation} = this.props;
      console.log("userInformation", userInformation);
      const myGroup = userInformation.role_id;
      const redirectGroups = [OWNER, CREW_LEADER, CREW_MEMBER];
      if (redirectGroups.includes(myGroup)) {
        await AsyncStorage.setItem('hasAutoRedirected', '1');
        navigate('Dashboard', {
          redirectedJobId: userInformation.last_viewed_job,
        });
      }
    }
  }

  /**
   * Component Render
   */
  render() {
    const {
      showInviteModal,
    } = this.state;

    const {
      isFetchingUserInfo,
      userInformation
    } = this.props;
    console.log("enable " + userInformation.is_subscribed);
    return (
      <View style={[styles.home]}>
        {
          isFetchingUserInfo ?
            <Spinner color={'red'}/>
            :
            <View style={[styles.mainContent]}>
              {/* Navigation */}
              <View style={[styles.navigationContainer]}>

                <AccessControl allowedGroups={[ADMIN, MANUFACTURER, DEALER]}>
                  <NavItem
                    route={'Accounts'}
                    icon={<Image style={[{width: 40.03, height: 40.03}]}
                                 source={require('_assets/images/accounts-icon-off.png')}/>}
                  />
                </AccessControl>

                <AccessControl allowedGroups={[OWNER, CREW_LEADER, CREW_MEMBER]}>
                  <NavItem
                    route={'Dashboard'}
                    icon={<Image style={[{width: 40.03, height: 40.03}]}
                                 source={require('_assets/images/dashboard-icon-off.png')}/>}
                  />
                </AccessControl>

                <AccessControl allowedGroups={[OWNER, CREW_LEADER, CREW_MEMBER]}>
                  <NavItem
                    route={'Jobs'}
                    icon={<Image style={[{width: 33, height: 43}]}
                                 source={require('_assets/images/jobs-icon-off.png')}/>}
                  />
                </AccessControl>

                <AccessControl allowedGroups={[OWNER]}>
                  <NavItem
                    route={'Crew Members'}
                    icon={<Image style={[{width: 39, height: 37}]}
                                 source={require('_assets/images/crew-members-icon-off.png')}/>}
                  />
                </AccessControl>

                <AccessControl allowedGroups={[OWNER]}>
                  <NavItem
                    route={'Devices'}
                    icon={<Image style={[{width: 42, height: 42}]}
                                 source={require('_assets/images/devices-icon-off.png')}/>}
                  />
                </AccessControl>
                <AccessControl allowedGroups={[ADMIN, MANUFACTURER, OWNER, CREW_LEADER, CREW_MEMBER]}>
                  <NavItem
                    route={'Sites'}
                    icon={<Image style={[{width: 40, height: 36}]}
                                 source={require('_assets/images/site-icon-off.png')}/>}
                  />
                </AccessControl>
                {/*<AccessControl allowedGroups={[ADMIN, DEALER, CREW_LEADER]}>*/}
                {/*  <NavItem*/}
                {/*    route={'Feedback'}*/}
                {/*    icon={<Image style={[{width: 40, height: 36}]}*/}
                {/*                 source={require('_assets/images/feedback-icon-off.png')}/>}*/}
                {/*  />*/}
                {/*</AccessControl>*/}

                <AccessControl allowedGroups={[MANUFACTURER, DEALER, OWNER, CREW_LEADER, CREW_MEMBER]}>
                  <NavItem
                    route={'Support'}
                    icon={
                    <Image
                      style={[{width: 40, height: 36}]}
                      source={require('_assets/images/feedback-icon-off.png')}/>}
                    onPress={() => {
                      const url = userInformation.manufacturer_website;
                      const fullUrl = url && !url.startsWith('http') ? `https://${url}` : url;
                      window.open(fullUrl, '_blank');
                    }}
                  />
                </AccessControl>

              </View>

              {/* Notifications */}
              <View style={[styles.notificationsContainer]}>
                <Notifications/>
              </View>
            </View>
        }

        {/* Invite Modal */}
        <View>
          <Modal
            isVisible={showInviteModal}
            hideTitle={true}
            hideExitIcon={true}
            content={showInviteModal ? <InviteModalContent/> : null}
          />
        </View>
      </View>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isFetchingUserInfo: state.user.isFetchingUserInfo,
    userInformation: state.user.userInfo,
  };
};

Home.propTypes = {
  navigation: PropTypes.any.isRequired,

  isFetchingUserInfo: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, null)(Home);


